/**
 * `<caos-record-header>` — the banner at the top of a record surface.
 * (Catalog: Views & filtering › RecordHeader — the object mark, record title, key fields, status, and tags at the head of a record.)
 *
 * An object icon (the `icon` slot, defaulting to a neutral document glyph) sits left
 * of the body. The object/type label is the DOMINANT heading (30px display) above the
 * record `heading` (26px in the accent) — the Dragon CRM record-page treatment — with
 * an inline `status` slot (a StatusPill / tags), a quiet `sub` line under the name
 * (CAOS-1882), a `fields` slot for the key read-only fields, and an `actions` slot
 * pinned right. The card carries a 3px accent top stripe
 * and card elevation (CRM_HEADER_CARD). Token-driven throughout. Styling seams:
 * `::part(header)`, `::part(icon)`, `::part(object-label)`, `::part(title)`,
 * `::part(status)`, `::part(sub)`, `::part(fields)`, `::part(actions)`.
 *
 * `sub` is an ATTRIBUTE and not a slot, unlike the three content regions, because it is
 * one line of text about the record and not a place to put components — the same reason
 * `heading` and `object-label` are attributes. It draws nothing when absent, empty, or
 * whitespace-only.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { below } from '../breakpoints.js';
import { makeIcon } from './icons.js';

const sheet = makeSheet(`
  :host { display: block; }

  .header {
    display: flex;
    align-items: flex-start;
    gap: var(--caos-space-4);
    padding: var(--caos-space-5);
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border-strong);
    /* Dragon CRM_HEADER_CARD: card elevation + a 3px accent top stripe. The
       border-top must follow the border shorthand (which resets all edges).
       Reads the accent token DIRECTLY. The stripe had a token of its own until
       CAOS-1680 — one that named this component, and whose value was this same
       accent in the base and in every brand that set it. */
    border-top: 3px solid var(--caos-color-accent);
    border-radius: var(--caos-radius-lg);
    box-shadow: var(--caos-shadow-card);
  }

  .icon {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--caos-size-lg-height);
    height: var(--caos-size-lg-height);
    border-radius: var(--caos-radius);
    background: var(--caos-color-accent-soft);
    color: var(--caos-color-accent-strong);
  }
  .icon svg { display: block; width: 24px; height: 24px; }
  ::slotted([slot="icon"]) { width: 24px; height: 24px; color: var(--caos-color-accent-strong); }
  /* no-icon mode: suppress the object-icon tile entirely (removed from the flex
     flow so it leaves no gap) — the Dragon record highlights band carries no icon
     tile. The icon capability itself is retained for consumers that opt into it. */
  :host([no-icon]) .icon { display: none; }

  .body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: var(--caos-space-1); }

  /* Dragon CRM record page (objectFramework objectTypeHeading): the object-type
     label is the DOMINANT heading of the highlight panel — 30px display 800, near-ink,
     above the record name. */
  .objectLabel {
    font-family: var(--caos-font-display);
    font-size: 30px; /* Dragon objectTypeHeading — off-scale, stays literal */
    font-weight: var(--caos-font-weight-display);
    line-height: 1.05;
    color: var(--caos-color-text);
  }
  .objectLabel:empty { display: none; }

  .titleRow { display: flex; align-items: center; gap: var(--caos-space-3); flex-wrap: wrap; }
  /* The record NAME sits below the object-type heading, in the accent (Dragon UI-295) */
  .title {
    font-family: var(--caos-font-display);
    font-weight: 700;
    font-size: 26px; /* Dragon record-name scale — off-scale, stays literal */
    line-height: 1.2;
    color: var(--caos-color-accent);
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .status { display: inline-flex; align-items: center; gap: var(--caos-space-2); flex-wrap: wrap; }
  .status:not(:has(*)) { display: none; }

  /* CAOS-1882 — the quiet second line, under the title row and above the key fields. Muted and at
     body scale, because it is context for the name rather than a peer of it (Dragon CRM record
     page, objectFramework's subtitle line).

     The :empty rule below is the whole of the "no value, no line" requirement, and it is
     structural rather than something the renderer has to remember: an empty line under a record's
     name is a gap in the header, not an answer, so it must not be possible to draw one by setting
     the attribute to a blank string. The same device .objectLabel above uses.

     (No backticks in this comment. It sits inside the makeSheet template literal, so one would
     close the string — and the error that follows is reported as a syntax error on the CSS line
     below rather than on the comment, which is where I spent the first look.) */
  .sub {
    /* --caos-text-body, the token KeyValueRow reads for the same scale. --caos-font-size-base looks
       like it ought to exist and does not, and the brand-reach check is what says so: every --caos-*
       a component reads must be defined in tokens.css AND in the workspace token file, so an
       invented name is a line a brand can never move. (No backticks here — see the note below.) */
    font-size: var(--caos-text-body);
    line-height: 1.5;
    color: var(--caos-color-text-muted);
    overflow-wrap: anywhere;
  }
  .sub:empty { display: none; }

  .fields { display: block; }
  .fields:not(:has(*)) { display: none; }
  ::slotted([slot="fields"]) { margin-top: var(--caos-space-1); }

  .actions { flex: none; display: flex; align-items: center; gap: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }

  @media ${below('small')} {
    .header { flex-wrap: wrap; }
    .actions { width: 100%; }
  }
`);

export class CaosRecordHeader extends CaosElement {
  static get observedAttributes(): string[] {
    return ['object-label', 'heading', 'sub', 'no-icon'];
  }

  private objectLabelEl: HTMLDivElement;
  private titleEl: HTMLHeadingElement;
  private subEl: HTMLDivElement;
  private defaultIcon: SVGSVGElement;

  constructor() {
    super(sheet);

    const header = document.createElement('div');
    header.className = 'header';
    header.setAttribute('part', 'header');

    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.setAttribute('part', 'icon');
    icon.setAttribute('aria-hidden', 'true');
    this.defaultIcon = makeIcon('record');
    const iconSlot = document.createElement('slot');
    iconSlot.name = 'icon';
    iconSlot.append(this.defaultIcon);
    icon.append(iconSlot);

    const body = document.createElement('div');
    body.className = 'body';

    this.objectLabelEl = document.createElement('div');
    this.objectLabelEl.className = 'objectLabel';
    this.objectLabelEl.setAttribute('part', 'object-label');

    const titleRow = document.createElement('div');
    titleRow.className = 'titleRow';
    this.titleEl = document.createElement('h1');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    const status = document.createElement('div');
    status.className = 'status';
    status.setAttribute('part', 'status');
    const statusSlot = document.createElement('slot');
    statusSlot.name = 'status';
    status.append(statusSlot);
    titleRow.append(this.titleEl, status);

    // CAOS-1882 — between the name and the key fields, which is where it belongs on both sides: it
    // qualifies the name above it, and the band below is a separate row of labelled values.
    this.subEl = document.createElement('div');
    this.subEl.className = 'sub';
    this.subEl.setAttribute('part', 'sub');

    const fields = document.createElement('div');
    fields.className = 'fields';
    fields.setAttribute('part', 'fields');
    const fieldsSlot = document.createElement('slot');
    fieldsSlot.name = 'fields';
    fields.append(fieldsSlot);

    body.append(this.objectLabelEl, titleRow, this.subEl, fields);

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    const actionsSlot = document.createElement('slot');
    actionsSlot.name = 'actions';
    actions.append(actionsSlot);

    header.append(icon, body, actions);
    this.root.append(header);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    this.objectLabelEl.textContent = this.getAttribute('object-label') ?? '';
    this.titleEl.textContent = this.getAttribute('heading') ?? '';
    // CAOS-1882 — trimmed, so an attribute carrying only whitespace leaves the element EMPTY and
    // the `:empty` rule hides it. Without the trim a single space is non-empty content and draws a
    // blank line under the record's name, which is the one outcome this line must never produce.
    this.subEl.textContent = (this.getAttribute('sub') ?? '').trim();
  }
}

define('caos-record-header', CaosRecordHeader);

declare global {
  interface HTMLElementTagNameMap {
    'caos-record-header': CaosRecordHeader;
  }
}
